import React from 'react'
import { useTranslation } from 'react-i18next'

import { BulletLabel, CopyToClipboard } from 'components'
import { setValueLanguage } from 'helpers/language'
import { setValue, getPhoneFormatter } from 'helpers/common'

import SkeletonBranchDetail from './SkeletonBranchDetail'

import { Branch } from 'features/Branch'
import { getBoxTitle } from 'helpers/language'

import { CONTACT_TYPE, FILE_REF_TYPE } from 'types'

interface Props {
  data: Branch
  loading: boolean
}

interface BoxItemProps {
  title: string | JSX.Element
  description?: string | boolean | number | JSX.Element | null
}

const base = 'f-branch-detail'

export const BoxItem = ({ title, description }: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

export const BoxAddressItem = ({
  title,
  description,
}: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-address-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

export const BoxDetailItem = ({
  title,
  description,
}: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-detail-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text-detail`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

const BranchDetail: React.FC<Props> = ({ data, loading }: Props) => {
  const { t } = useTranslation(['branch', 'common', 'company'])

  const {
    code,
    isActive,
    name: branchName,
    companyName,
    contacts,
    addresses,
    location = '',
    contactName,
    detail,
  } = data

  const {
    address,
    countryEn,
    countryTh,
    provinceEn,
    provinceTh,
    districtEn,
    districtTh,
    subDistrictTh,
    subDistrictEn,
    postalCode,
  } = addresses[0]

  const emailContact = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.EMAIL,
  )
  const branchPhone = contacts.find((contact) => {
    return (
      contact.type === CONTACT_TYPE.PHONE &&
      contact.refType === FILE_REF_TYPE.BRANCH
    )
  })
  const _contactPhone = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.CONTACT_PHONE,
  )
  const phone = branchPhone?.number
    ? getPhoneFormatter(branchPhone.number as string)
    : '-'

  const contactPhone = _contactPhone?.number
    ? getPhoneFormatter(_contactPhone.number as string)
    : '-'

  const email = emailContact?.email

  const _detail = detail ? detail : t('common:address.noRemark.label')

  const BranchInfo = () => {
    const branchInfo = [
      { title: 'branch:id.label', description: code },
      {
        title: 'branch:status.label',
        description: (
          <BulletLabel
            isActive={isActive}
            isShowLabel={true}
            className={`${base}__status-description`}
          />
        ),
      },
      { title: 'branch:name.label', description: branchName },
      {
        title: 'common:phone.label',
        description: phone,
      },
      { title: 'company:name.label', description: companyName },
    ]

    return (
      <div className={`${base}__branch-info-content`}>
        <div className={`${base}__title-layout`}>
          <span className={`${base}__branch-detail-title`}>
            {t('branch:info.label')}
          </span>
        </div>
        <div className={`${base}__description-layout`}>
          {branchInfo.map((data, index) => {
            return (
              <BoxItem
                key={index}
                title={data.title}
                description={data.description}
              />
            )
          })}
        </div>
      </div>
    )
  }

  const BranchAddress = () => {
    const country = setValueLanguage(countryTh, countryEn)
    const province = setValueLanguage(provinceTh, provinceEn)
    const district = setValueLanguage(districtTh, districtEn)
    const subDistrict = setValueLanguage(subDistrictTh, subDistrictEn)

    const branchAddress = [
      { title: 'common:address.country.label', description: country },
      { title: 'common:address.province.label', description: province },
      { title: 'common:address.district.label', description: district },
      {
        title: 'common:address.subdistrict.label',
        description: subDistrict,
      },
      { title: 'common:address.postalCode.label', description: postalCode },
      { title: 'common:email.label', description: email },
      {
        title: 'common:location.label',
        description: <CopyToClipboard url={location} />,
      },
    ]

    return (
      <div className={`${base}__branch-address-content`}>
        <div className={`${base}__title-layout`}>
          <span className={`${base}__branch-detail-title`}>
            {t('common:address.label')}
          </span>
        </div>
        <div>
          <div className={`${base}__description-layout`}>
            <BoxAddressItem
              title='common:address.label'
              description={address}
            />
            {branchAddress.map((data, index) => {
              return (
                <BoxItem
                  key={index}
                  title={data.title}
                  description={data.description}
                />
              )
            })}
          </div>
          <BoxDetailItem
            title='common:address.remark.label'
            description={_detail}
          />
        </div>
      </div>
    )
  }

  const BranchContact = () => {
    const branchContact = [
      { title: 'common:contactName.label', description: contactName },
      {
        title: 'common:phone.label',
        description: contactPhone,
      },
    ]
    return (
      <div className={`${base}__branch-contact-content`}>
        <div className={`${base}__title-layout`}>
          <span className={`${base}__branch-detail-title`}>
            {t('branch:contactInformation.label')}
          </span>
        </div>
        <div className={`${base}__description-layout`}>
          {branchContact.map((data, index) => {
            return (
              <BoxItem
                key={index}
                title={data.title}
                description={data.description}
              />
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={base}>
      {loading ? (
        <SkeletonBranchDetail />
      ) : (
        <React.Fragment>
          <BranchInfo />
          <BranchAddress />
          <BranchContact />
        </React.Fragment>
      )}
    </div>
  )
}

export default BranchDetail
